import React, { useState, useEffect } from 'react';
import { StudentAppLayout } from '../../components/StudentAppLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { useLazyQuery } from '@soap/modules';
import { User, Mail, LogOut } from 'lucide-react';
import { scheduleLogoutFromHomeHash } from '../../../constants/postLogoutReturnUrl';


export function StudentSettingsPage() {
  const [getProfile, gotProfile] = useLazyQuery("C166v1_GetUserProfile", {});
  const [viewState, setViewState] = useState('loading');

  useEffect(() => {
    if (gotProfile) {
      setViewState('loaded');
    }
  }, [gotProfile]);

  const handleLogOut = () => {
    scheduleLogoutFromHomeHash();
  };

  if (viewState === 'loading') {
    return (
      <StudentAppLayout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="space-y-6">
              <div className="h-64 bg-gray-200 rounded"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </StudentAppLayout>
    );
  }

  return (
    <StudentAppLayout>
      <div className="p-6 max-w-2xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="mt-2 text-gray-600">
            Manage your student account{gotProfile?.e138_FirstName ? `, ${gotProfile.e138_FirstName}` : ''}
          </p>
        </div>

        <div className="space-y-6">
          {/* Account Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <User className="h-5 w-5 mr-2" />
                Account Information
              </CardTitle>
              <CardDescription>
                Your personal details and account settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name</Label>
                  <Input 
                    id="firstName" 
                    type="text" 
                    placeholder="Enter your first name"
                    defaultValue={gotProfile?.e138_FirstName || ''}
                    disabled
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input 
                    id="lastName" 
                    type="text" 
                    placeholder="Enter your last name"
                    defaultValue={gotProfile?.e138_LastName || ''}
                    disabled
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="Enter your email"
                    defaultValue={gotProfile?.e138_Email || ''}
                    className="pl-10"
                    disabled
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Contact your teacher to update your email address
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Account Type */}
          <Card>
            <CardHeader>
              <CardTitle>Account Type</CardTitle>
              <CardDescription>
                Your current account permissions and access level
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-3 p-4 bg-action-blue/10 rounded-lg">
                <div className="w-3 h-3 bg-action-blue rounded-full"></div>
                <div>
                  <p className="font-medium text-action-blue-dark">Student Account</p>
                  <p className="text-sm text-action-blue-dark">
                    Access to assigned assessments and learning materials
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Account Actions</CardTitle>
              <CardDescription>
                Manage your account and session
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={handleLogOut}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Log Out
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Help */}
          <Card>
            <CardHeader>
              <CardTitle>Need Help?</CardTitle>
              <CardDescription>
                Get support with your account or assignments
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm text-gray-600">
                <p>• Contact your teacher for assignment questions</p>
                <p>• Reach out to technical support for account issues</p>
                <p>• Check the help section for common questions</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </StudentAppLayout>
  );
}